import {
  ApiChannelDefault,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box } from "@mui/material";
import CarouselCard from "Components/UI/CarouselCard";
import Loading from "Components/Loading/Loading";
import { StandardAnalyticsChartStatCard, table5Rows } from "features/analytics";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_EMAIL_CAMPAIGN_TABLE,
  CHART_KEY_DASHBOARD_EMAIL_CAMPAIGN_TREND,
  CHART_KEY_DASHBOARD_EMAIL_RETURNING_STAT,
  CHART_KEY_DASHBOARD_EMAIL_SESSIONS_STAT,
  CHART_KEY_DASHBOARD_EMAIL_VISIT_PURCHASE_STAT,
} from "features/analytics/constants/chartKeys";
import { useChannels } from "Hooks/useChannels";
import { useMemo } from "react";

interface IEmailTabProps {
  attributes: ApiChartAttribute[];
}

function EmailTab({ attributes }: IEmailTabProps) {
  const { channels } = useChannels();
  const emailChannelName = useMemo(
    () =>
      channels.find((c) => c._default === ApiChannelDefault.EMAIL)?.name || "",
    [channels],
  );

  const emailAttributes = useMemo(() => {
    if (!emailChannelName) {
      return attributes;
    }

    return [
      ...attributes,
      {
        key: ApiDimension.DIMENSION_CHANNEL,
        value: emailChannelName,
      },
    ];
  }, [attributes, emailChannelName]);

  if (!emailChannelName) {
    return (
      <Box
        sx={{
          minHeight: 280,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loading />
      </Box>
    );
  }

  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage="No key stats available"
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_EMAIL_SESSIONS_STAT}
            staticAttributes={emailAttributes}
            chart={{
              name: "Email Sessions",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "Sessions",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_EMAIL_VISIT_PURCHASE_STAT}
            staticAttributes={emailAttributes}
            chart={{
              name: "Visit to Purchase Rate",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE],
                },
              ],
              displayOptions: {
                statTitle: "Visit to Purchase Rate",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_EMAIL_RETURNING_STAT}
            staticAttributes={emailAttributes}
            chart={{
              name: "Returning Customer %",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_RETURNING_NINETY_DAY_CUSTOMER_PERCENTAGE,
                  ],
                },
              ],
              displayOptions: {
                statTitle: "Returning Customer %",
              },
              comparison: true,
            }}
          />,
        ]}
        loading={false}
      />

      <Box sx={{ height: 400, flexShrink: 0 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_EMAIL_CAMPAIGN_TREND}
          staticAttributes={emailAttributes}
          chart={{
            name: "Sessions & Revenue by Email Campaign",
            dimension: [ApiDimension.DIMENSION_CAMPAIGN],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            limit: 10,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_REVENUE],
              },
            ],
            displayOptions: {
              noFilters: true,
              metricsOrder: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_REVENUE,
              ],
            },
          }}
        />
      </Box>

      <Box sx={{ height: table5Rows, flexShrink: 0 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_EMAIL_CAMPAIGN_TABLE}
          staticAttributes={emailAttributes}
          chart={{
            name: "Email Campaigns",
            dimension: [ApiDimension.DIMENSION_CAMPAIGN],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              rowsPerPage: 0,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default EmailTab;
